<template>
  <v-stepper v-model="importStep" vertical class="import-csv--stepper">
    <v-stepper-step :complete="importStep > 1" step="1">
      {{ $t('validate-csv') }}
    </v-stepper-step>

    <v-stepper-content step="1">
      <v-card>
        <v-card-text>
          <v-layout align-top fill-width justify-center row>
            <form enctype="multipart/form-data">
              <input @change="enableStep2" accept="text/csv" name="csvUploadInput" ref="fileInput" type="file"/>
            </form>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click.stop="cancelImport" color="primary" text>{{ $t('cancel') }}</v-btn>
          <v-btn :disabled="step2Disabled" @click="moveToStep2" color="primary">{{ $t('validate') }}
            <v-icon>navigate_next</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-stepper-content>

    <v-stepper-step :complete="importStep > 2" step="2">
      {{ $t('approve-changes') }}
      <small v-if="!summaryData && importStep === 2">{{ $t('loading-summary') }}</small>
    </v-stepper-step>

    <v-stepper-content step="2">
      <div v-if="!summaryData">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <v-card v-else-if="!summaryData.error">
        <v-layout align-top fill-width justify-center row>
          <v-chip color="green" text-color="white" v-if="summaryData.newEntities.length > 0">
            <v-avatar class="green darken-3">{{summaryData.newEntities.length}}</v-avatar>
            <v-icon>add</v-icon>
          </v-chip>
          <v-chip color="amber" text-color="white" v-if="summaryData.changedEntities.length > 0">
            <v-avatar class="amber darken-3">{{summaryData.changedEntities.length}}</v-avatar>
            <v-icon>edit</v-icon>
          </v-chip>
          <v-chip color="red" text-color="white" v-if="summaryData.errorEntities.length > 0">
            <v-avatar class="red darken-3">{{summaryData.errorEntities.length}}</v-avatar>
            <v-icon>warning</v-icon>
          </v-chip>
          <v-chip color="grey" text-color="white" v-if="(summaryData.unchangedEntities.length) > 0">
            <v-avatar class="grey darken-3">{{summaryData.unchangedEntities.length}}</v-avatar>
            <v-icon>check</v-icon>
          </v-chip>
        </v-layout>
        <v-card-text v-if="!!normalizedItems.data.length && !!normalizedItems.headers.length">
          <v-data-table
            :headers="normalizedItems.headers"
            :items="normalizedItems.data"
            :footer-props="{ 'items-per-page-text': $t('rows-per-page'), 'items-per-page-options': [20,50,100,{ text: $t('all'), value: -1 }] }"
          >
            <template slot="item" slot-scope="props">
              <tr>
                <td>
                  <v-tooltip bottom v-if="props.item['icon_tooltip']">
                    <template v-slot:activator="{ on }">
                      <v-icon v-if="props.item.icon" v-on="on" color="grey">{{props.item.icon}}</v-icon>
                    </template>
                    {{$t(props.item['icon_tooltip'])}}
                  </v-tooltip>
                  <v-icon v-else color="grey">{{props.item.icon}}</v-icon>
                </td>
                <td>{{props.item.id}}</td>
                <td>{{ getPropDisplay(props.item.name) }}</td>
                <td v-for="key in getGeneratableKeys(props.item)" class="text-right" :key="key">
                  {{ getPropDisplay(props.item[key]) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-card-text v-if="!step3Invalid">
            {{ $t('apply-changes-notice') }}
          </v-card-text>
          <v-card-text v-else>
            {{ $t('no-changes-to-be-made') }}
          </v-card-text>
          <v-spacer></v-spacer>
          <v-btn @click.stop="cancelImport" v-if="!step3Invalid" color="primary" text>{{ $t('restart') }}</v-btn>
          <v-btn @click.stop="cancelImport" v-else color="primary" text>{{ $t('restart') }}</v-btn>
          <v-btn @click.stop="moveToStep3" :disabled="step3Invalid" color="primary">{{ $t('apply-import-changes') }}
            <v-icon>navigate_next</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else>
        <v-alert color="red" type="error">
          {{summaryData.error}}
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click.stop="cancelImport" color="primary">{{ $t('restart') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-stepper-content>

    <v-stepper-step step="3">
      {{ $t('finished') }}
    </v-stepper-step>

    <v-stepper-content step="3">
      <div v-if="!importResult">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <v-alert
        :type="importResult.status"
        :color="importResult.color"
        v-else>
        {{importResult.message}}
      </v-alert>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click.stop="cancelImport" color="primary" text>{{ $t('restart') }}</v-btn>
        <v-btn :disabled="!importResult" @click.stop="$router.push({name: `${entity}s`})" color="primary">{{ $t('show-results') }}
          <v-icon>navigate_next</v-icon>
        </v-btn>
      </v-card-actions>
    </v-stepper-content>
  </v-stepper>
</template>

<style scoped>
  .import-csv--stepper {
    box-shadow: none;
  }
</style>

<script>
import displayHelpers from '../../helpers/display-helpers.es6'

export default {
  name: 'import-csv',
  props: {
    entity: {
      type: String,
      required: true
    },
    importResult: {
      type: Object,
      default: undefined
    }
  },
  mixins: [displayHelpers],
  data () {
    return {
      importStep: 1,
      step2Disabled: true,
      nonGeneratableKeys: [
        'id', 'name', 'icon', 'icon_color', 'icon_tooltip'
      ],
      summaryData: undefined,
      normalizedItems: {
        headers: [],
        data: []
      }
    }
  },
  computed: {
    step3Invalid () {
      return !this.summaryData.newEntities.length && !this.summaryData.changedEntities.length
    }
  },
  methods: {
    enableStep2 () {
      const file = this.$refs['fileInput'].files[0]
      if (file) {
        this.step2Disabled = false
      }
    },
    cancelImport () {
      this.$refs['fileInput'].value = null
      this.importStep = 1
      this.step2Disabled = true
      this.summaryData = undefined
      this.$emit('importCancelled')
    },
    moveToStep2 () {
      this.importStep = 2
      this.$emit('validateCSV', this.$refs['fileInput'].files[0])
    },
    moveToStep3 () {
      this.importStep = 3
      this.$emit('applyCSV', this.$refs['fileInput'].files[0])
    },
    getPropDisplay (prop) {
      if (!prop || ((prop.hasOwnProperty('newValue') && !prop.newValue) && !prop.oldValue)) {
        return ''
      }

      if (prop.newValue !== prop.oldValue) {
        return `${prop.oldValue || null} -> ${prop.newValue || null}`
      } else if (prop.newValue) {
        return prop.newValue
      } else {
        return prop
      }
    },
    getGeneratableKeys (item) {
      return Object.keys(item).filter(this.getIsGeneratableKey)
    },
    getIsGeneratableKey (key) {
      if (this.nonGeneratableKeys.includes(key)) {
        return false
      }

      return true
    },
    normalizeData (data) {
      this.summaryData = data
      const combinedItems = [...data.newEntities, ...data.changedEntities, ...data.errorEntities, ...data.unchangedEntities]
      if (data.headers.length && combinedItems.length) {
        const localizedHeaders = data.headers.reduce((newHeaders, oldHeader) => {
          const newHeader = { ...oldHeader }
          newHeader.text = this.$t(oldHeader.text)
          return [...newHeaders, newHeader]
        }, [])

        const normalizedItemsData = combinedItems.reduce((items, item) => {
          const newItem = {
            icon: item['icon'],
            'icon_tooltip': item['icon_tooltip'],
            id: item['id'],
            name: item['name']
          }
          for (const header of localizedHeaders) {
            const headerKey = header.value
            if (this.getIsGeneratableKey(headerKey)) {
              newItem[headerKey] = item[headerKey] || null
            }
          }
          return [...items, newItem]
        }, [])

        this.$set(this.normalizedItems, 'headers', localizedHeaders)
        this.$set(this.normalizedItems, 'data', normalizedItemsData)
      }
    }
  }
}
</script>
